/**
 * Rappel de fermeture de caisse — fenêtre centrée, staff.
 *
 * Quand une caisse est encore OUVERTE après l'heure de fermeture du magasin (ou un jour normalement
 * fermé), affiche une fenêtre centrée : « Fermer la caisse » ou report 10 min / 1 h / 2 h.
 * - Report persistant au rechargement (localStorage, par magasin) ; à l'expiration, la fenêtre revient.
 * - Réutilise l'horaire GLOBAL (HoursLive) + le statut caisse (/caisse/status) + le gating de rôle
 *   (shouldShowCaisseInHeader : techniciens exclus). Monté UNE fois dans Layout -> aucun doublon.
 * - Aucune modification serveur : la fermeture renvoie vers le parcours de fermeture existant.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, Clock, AlarmClock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import useActiveMagasin from '../../hooks/useActiveMagasin';
import { shouldShowCaisseInHeader } from '../../utils/roleRouting';
import {
  currentCayenne, effectiveDayHours, pastClosing, readSnoozeUntil, writeSnoozeUntil, clearSnooze,
} from '../../features/caisse/caisseReminderLogic';
import { cayenneYmd } from '../landing/HoursLive';

const POLL_MS = 60000;   // statut caisse (aligné sur CaisseHeaderChip)
const TICK_MS = 30000;   // ré-évaluation locale (expiration report / passage de l'heure de fermeture)

const CaisseFermetureReminder = () => {
  const { api, user } = useAuth();
  const { code, isAll, allMagasins } = useActiveMagasin();
  const navigate = useNavigate();
  const location = useLocation();
  const [openByStore, setOpenByStore] = useState({});
  const [nowMs, setNowMs] = useState(() => Date.now());

  const allowed = shouldShowCaisseInHeader(user);
  const storesKey = code || (isAll ? 'M1,M2' : '');

  // Sondage du statut caisse (magasin actif, ou M1+M2 pour la vue « tous magasins »).
  useEffect(() => {
    if (!api || !user || !allowed || !storesKey) return undefined;
    const stores = storesKey.split(',');
    let cancelled = false;
    const fetchAll = async () => {
      try {
        const entries = await Promise.all(stores.map(async (s) => {
          try {
            const r = await api.get(`/caisse/status?magasin_id=${encodeURIComponent(s)}`);
            return [s, !!r.data?.is_open];
          } catch { return [s, false]; }
        }));
        if (!cancelled) setOpenByStore(Object.fromEntries(entries));
      } catch { /* réseau indispo -> on ne force rien */ }
    };
    fetchAll();
    const t = setInterval(fetchAll, POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [api, user, allowed, storesKey]);

  // Tick local : fait revenir la fenêtre à l'expiration du report et au passage de l'heure de fermeture.
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), TICK_MS);
    return () => clearInterval(t);
  }, []);

  // Ne pas recouvrir la page d'ouverture/fermeture de caisse (le staff y agit déjà).
  const onCaissePage = /^\/(fermer|ouvrir)-caisse/.test(location.pathname || '');
  if (!allowed || !storesKey || onCaissePage) return null;

  // Horaires EFFECTIFS par magasin (exception par date > hebdo par magasin > défaut global).
  const cn = currentCayenne();
  const ymd = cayenneYmd();
  const magByCode = (allMagasins || []).reduce((acc, m) => { acc[String(m.code || '').toUpperCase()] = m; return acc; }, {});
  let target = null;
  let pc = null;
  for (const s of storesKey.split(',')) {
    const cand = pastClosing(cn, effectiveDayHours(magByCode[s.toUpperCase()] || null, cn.d, ymd));
    if (openByStore[s] && cand.past && nowMs >= readSnoozeUntil(s)) { target = s; pc = cand; break; }
  }
  if (!target) return null;

  const snooze = (minutes) => { writeSnoozeUntil(target, Date.now() + minutes * 60000); setNowMs(Date.now()); };
  const goClose = () => { clearSnooze(target); navigate(`/fermer-caisse?magasin_id=${encodeURIComponent(target)}`); };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
      data-testid="caisse-reminder-overlay"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-gray-900 border border-amber-500/40 rounded-2xl max-w-md w-full p-6 shadow-2xl" data-testid="caisse-reminder-modal">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 rounded-xl bg-amber-500/15 text-amber-400 shrink-0"><AlarmClock className="w-6 h-6" /></div>
          <h2 className="text-lg font-bold text-white">Caisse {target} encore ouverte</h2>
        </div>
        <p className="text-sm text-gray-300">
          {pc.closeHour != null
            ? `Il est plus de ${pc.closeHour} h (heure de fermeture, Cayenne) et la caisse ${target} est toujours ouverte.`
            : `La caisse ${target} est ouverte un jour normalement fermé (heure de Cayenne).`}
        </p>
        <p className="text-xs text-gray-500 mt-1 mb-5">Pense à fermer la caisse, ou reporte le rappel.</p>
        <button
          type="button"
          onClick={goClose}
          data-testid="caisse-reminder-close"
          className="w-full mb-3 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-red-600 hover:bg-red-500 text-white font-semibold min-h-[44px]"
        >
          <Lock className="w-4 h-4" /> Fermer la caisse
        </button>
        <div className="grid grid-cols-3 gap-2">
          {[['10 min', 10], ['1 h', 60], ['2 h', 120]].map(([label, mins]) => (
            <button
              key={mins}
              type="button"
              onClick={() => snooze(mins)}
              data-testid={`caisse-reminder-snooze-${mins}`}
              className="inline-flex items-center justify-center gap-1 px-2 py-2 rounded-lg border border-white/10 bg-black/20 text-gray-300 hover:text-white text-sm min-h-[40px]"
            >
              <Clock className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CaisseFermetureReminder;
